import React, { useState, useEffect } from 'react';
import './EditContent.css';
import './HomePage.css';
import CommonHeader from './CommonHeader';
import CommonFooter from './CommonFooter';
import Loader from './Loader';
import bannerService, { BannerData } from '../services/bannerService';
import ceoService, { CeoData } from '../services/ceoService';
import chairmanService, { ChairmanData } from '../services/chairmanService';
import applicationService, { ApplicationData } from '../services/applicationService';
import BannerManagement from './BannerManagement';
import JwilUpdatesManagement from './JwilUpdatesManagement';
import CompanyPoliciesManagement from './CompanyPoliciesManagement';
import CategoryManagement from './CategoryManagement';
import EProcedureManagement from './EProcedureManagement';
import SnippetAlbumManagement from './SnippetAlbumManagement';
import EventsManagement from './EventsManagement';
import RewardRecognitionManagement from './RewardRecognitionManagement';
import LeadersManagement from './LeadersManagement';
import RoleManagement from './RoleManagement';
import UserManagement from './UserManagement';
import NewsletterManagement from './NewsletterManagement';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import rolesService from '../services/rolesService';

const EditContent: React.FC = () => {
  const [activeSection, setActiveSection] = useState('banner');
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [allowedTabs, setAllowedTabs] = useState<string[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  
  // Banner state management
  const [bannerData, setBannerData] = useState<BannerData | null>(null);
  const [bannerContent, setBannerContent] = useState('');
  const [bannerVisible, setBannerVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // CEO state management
  const [ceoData, setCeoData] = useState<CeoData | null>(null);
  const [ceoTitle, setCeoTitle] = useState('');
  const [ceoContent, setCeoContent] = useState('');
  const [ceoName, setCeoName] = useState('');
  const [ceoPosition, setCeoPosition] = useState('');
  const [ceoImage, setCeoImage] = useState<File | null>(null);
  const [ceoImagePreview, setCeoImagePreview] = useState<string>('');
  
  // Chairman state management
  const [chairmanData, setChairmanData] = useState<ChairmanData | null>(null);
  const [chairmanTitle, setChairmanTitle] = useState('');
  const [chairmanContent, setChairmanContent] = useState('');
  const [chairmanName, setChairmanName] = useState('');
  const [chairmanPosition, setChairmanPosition] = useState('');
  const [chairmanImage, setChairmanImage] = useState<File | null>(null);
  const [chairmanImagePreview, setChairmanImagePreview] = useState<string>('');

  // Application state management
  const [applications, setApplications] = useState<ApplicationData[]>([]);
  const [editingApp, setEditingApp] = useState<ApplicationData | null>(null);
  const [appIconFile, setAppIconFile] = useState<File | null>(null);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingRowData, setEditingRowData] = useState<ApplicationData | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<number, ApplicationData>>(new Map());
  const [draggedItem, setDraggedItem] = useState<ApplicationData | null>(null);
  const [showDeleteAppModal, setShowDeleteAppModal] = useState(false);
  const [appToDelete, setAppToDelete] = useState<ApplicationData | null>(null);
  const [appSearchQuery, setAppSearchQuery] = useState('');
  const [appCurrentPage, setAppCurrentPage] = useState(1);
  const [appItemsPerPage] = useState(10);
  
  // Track form changes for Banner, CEO, and Chairman
  const [originalBannerData, setOriginalBannerData] = useState<{ content: string; visible: boolean } | null>(null);
  const [originalCeoData, setOriginalCeoData] = useState<{ title: string; content: string; name: string; position: string } | null>(null);
  const [originalChairmanData, setOriginalChairmanData] = useState<{ title: string; content: string; name: string; position: string } | null>(null);
  const [hasBannerChanges, setHasBannerChanges] = useState(false);
  const [hasCeoChanges, setHasCeoChanges] = useState(false);
  const [hasChairmanChanges, setHasChairmanChanges] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    window.location.href = '/';
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchFocus = () => {
    setShowSearchDropdown(true);
  };

  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.relatedTarget && (e.relatedTarget as HTMLElement).closest('.search-results-dropdown')) {
      return;
    }
    setTimeout(() => setShowSearchDropdown(false), 200);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSearchDropdown(false);
  };

  // Load user permissions
  useEffect(() => {
    loadUserPermissions();
  }, []);

  // Load data on component mount
  useEffect(() => {
    if (!isLoadingPermissions) {
      loadBannerData();
      loadCeoData();
      loadChairmanData();
      loadApplications();
    }
  }, [isLoadingPermissions]);

  const loadUserPermissions = async () => {
    try {
      // Get user profile to get role info
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      if (!token) {
        console.error('No JWT token found');
        setIsLoadingPermissions(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Full API response:', data);
        
        // Handle different response structures
        const user = data.user || data;
        const tabPermissions = user.tabPermissions || user.tab_permissions || [];
        const roleName = user.roleName || user.role_name || user.role || '';
        
        console.log('User role:', roleName);
        console.log('User tab permissions:', tabPermissions);
        
        setUserRole(roleName);
        setAllowedTabs(tabPermissions);
        
        // Set first allowed tab as active, or default to 'banner'
        if (tabPermissions.length > 0) {
          setActiveSection(tabPermissions[0]);
        } else if (roleName === 'Super Admin') {
          // Super Admin with no explicit permissions sees all tabs
          setActiveSection('banner');
        }
      } else {
        console.error('Failed to load user permissions');
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  // Check if user has permission for a tab
  const hasTabPermission = (tabId: string): boolean => {
    // Super Admin has access to all tabs
    if (userRole === 'Super Admin') return true;
    return allowedTabs.includes(tabId);
  };

  const loadBannerData = async () => {
    setIsLoading(true);
    try {
      const response = await bannerService.getBanner();
      console.log('Load Banner Response:', response); // Debug log
      
      if (response.success && response.data) {
        console.log('Banner Data:', {
          content: response.data.content,
          is_visible: response.data.is_visible
        }); // Debug log
        
        setBannerData(response.data);
        setBannerContent(response.data.content || '');
        setBannerVisible(response.data.is_visible);
        
        // Store original data for change detection
        setOriginalBannerData({
          content: response.data.content || '',
          visible: response.data.is_visible
        });
      } else {
        console.log('No banner data found, using defaults'); // Debug log
        // Set default values if no banner exists
        setBannerContent('');
        setBannerVisible(true);
        setOriginalBannerData({
          content: '',
          visible: true
        });
      }
      setHasBannerChanges(false);
    } catch (error) {
      console.error('Error loading banner data:', error);
      setSaveMessage('Error loading banner data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveBanner = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    console.log('Saving banner with:', { content: bannerContent, visible: bannerVisible }); // Debug log
    
    try {
      const response = await bannerService.updateBannerFull(bannerContent, bannerVisible);
      console.log('Banner Update Response:', response); // Debug log
      
      if (response.success) {
        // Update all banner states from response
        setBannerData(response.data);
        setBannerContent(response.data.content);
        setBannerVisible(response.data.is_visible);
        
        setSaveMessage('Banner updated successfully!');
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSaveMessage(''), 5000);
        
        // Update original data and reset change flag
        setOriginalBannerData({
          content: response.data.content,
          visible: response.data.is_visible
        });
        setHasBannerChanges(false);
      } else {
        const errorMessage = response.message || 'Unknown error occurred';
        setSaveMessage('Error updating banner: ' + errorMessage);
        console.error('Banner Update Error:', response);
      }
    } catch (error) {
      console.error('Error saving banner:', error);
      setSaveMessage('Error saving banner changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelBanner = () => {
    if (bannerData) {
      setBannerContent(bannerData.content);
      setBannerVisible(bannerData.is_visible);
    } else {
      setBannerContent('');
      setBannerVisible(true);
    }
    setSaveMessage('');
  };

  const loadCeoData = async () => {
    setIsLoading(true);
    try {
      const response = await ceoService.getCeo();
      if (response.success && response.data) {
        console.log('CEO data loaded:', response.data); // Debug log
        setCeoData(response.data);
        setCeoTitle(response.data.title);
        setCeoContent(response.data.content);
        setCeoName(response.data.ceo_name);
        setCeoPosition(response.data.ceo_position);
        
        const imageUrl = ceoService.getImageUrl(response.data.icon);
        console.log('Setting CEO image preview to:', imageUrl); // Debug log
        setCeoImagePreview(imageUrl);
        
        // Store original data for change detection
        setOriginalCeoData({
          title: response.data.title,
          content: response.data.content,
          name: response.data.ceo_name,
          position: response.data.ceo_position
        });
      } else {
        console.log('No CEO data found, setting defaults'); // Debug log
        // Set default values if no CEO exists
        setCeoTitle('MD & CEO');
        setCeoContent('');
        setCeoName('');
        setCeoPosition('');
        setCeoImagePreview('/assets/images/md-ceo.svg');
        
        setOriginalCeoData({
          title: 'MD & CEO',
          content: '',
          name: '',
          position: ''
        });
      }
      setHasCeoChanges(false);
    } catch (error) {
      console.error('Error loading CEO data:', error);
      setSaveMessage('Error loading CEO data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChairmanData = async () => {
    setIsLoading(true);
    try {
      const response = await chairmanService.getChairman();
      if (response.success && response.data) {
        console.log('Chairman data loaded:', response.data);
        setChairmanData(response.data);
        setChairmanTitle(response.data.title);
        setChairmanContent(response.data.content);
        setChairmanName(response.data.ceo_name); // Reusing ceo_name field
        setChairmanPosition(response.data.ceo_position); // Reusing ceo_position field
        
        const imageUrl = chairmanService.getImageUrl(response.data.icon);
        console.log('Setting Chairman image preview to:', imageUrl);
        setChairmanImagePreview(imageUrl);
        
        // Store original data for change detection
        setOriginalChairmanData({
          title: response.data.title,
          content: response.data.content,
          name: response.data.ceo_name,
          position: response.data.ceo_position
        });
      } else {
        console.log('No Chairman data found, setting defaults');
        // Set default values if no Chairman exists
        setChairmanTitle('Chairman');
        setChairmanContent('');
        setChairmanName('');
        setChairmanPosition('');
        setChairmanImagePreview('/assets/images/md-ceo.svg');
        
        setOriginalChairmanData({
          title: 'Chairman',
          content: '',
          name: '',
          position: ''
        });
      }
      setHasChairmanChanges(false);
    } catch (error) {
      console.error('Error loading Chairman data:', error);
      setSaveMessage('Error loading Chairman data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadApplications = async () => {
    setIsLoading(true);
    try {
      const response = await applicationService.getApplications();
      if (response.success) {
        // Sort by title in ascending order
        const sortedApps = response.data.sort((a, b) => 
          a.title.localeCompare(b.title)
        );
        setApplications(sortedApps);
      } else {
        console.error('Error loading applications:', response.message);
        setSaveMessage('Error loading applications');
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      setSaveMessage('Error loading applications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCeo = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    // Client-side validation
    if (!ceoContent.trim()) {
      setSaveMessage('Error: CEO message content is required');
      setIsSaving(false);
      return;
    }
    
    if (!ceoName.trim()) {
      setSaveMessage('Error: CEO name is required');
      setIsSaving(false);
      return;
    }
    
    if (!ceoPosition.trim()) {
      setSaveMessage('Error: CEO position is required');
      setIsSaving(false);
      return;
    }
    
    try {
      console.log('Sending CEO data:', { ceoTitle, ceoContent, ceoName, ceoPosition }); // Debug log
      
      const response = await ceoService.updateCeo(
        ceoTitle,
        ceoContent,
        ceoName,
        ceoPosition,
        ceoImage || undefined
      );
      
      console.log('CEO Update Response:', response); // Debug log
      
      if (response.success) {
        setCeoData(response.data);
        setSaveMessage('CEO data updated successfully!');
        setCeoImage(null); // Clear selected file after successful upload
        setCeoImagePreview(ceoService.getImageUrl(response.data.icon));
        
        // Update original data and reset change flag
        setOriginalCeoData({
          title: ceoTitle,
          content: ceoContent,
          name: ceoName,
          position: ceoPosition
        });
        setHasCeoChanges(false);
        
        // Force refresh of the file input
        const fileInput = document.getElementById('ceoImage') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSaveMessage(''), 5000);
      } else {
        const errorMessage = response.message || 'Unknown error occurred';
        setSaveMessage('Error updating CEO data: ' + errorMessage);
        console.error('CEO Update Error:', response);
      }
    } catch (error) {
      console.error('Error saving CEO data:', error);
      setSaveMessage('Error saving CEO data changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelCeo = () => {
    if (ceoData) {
      setCeoTitle(ceoData.title);
      setCeoContent(ceoData.content);
      setCeoName(ceoData.ceo_name);
      setCeoPosition(ceoData.ceo_position);
      setCeoImagePreview(ceoService.getImageUrl(ceoData.icon));
    } else {
      setCeoTitle('MD & CEO');
      setCeoContent('');
      setCeoName('');
      setCeoPosition('');
      setCeoImagePreview('/assets/images/md-ceo.svg');
    }
    setCeoImage(null);
    setSaveMessage('');
  };

  const handleSaveChairman = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    // Client-side validation
    if (!chairmanContent.trim()) {
      setSaveMessage('Error: Chairman message content is required');
      setIsSaving(false);
      return;
    }
    
    if (!chairmanName.trim()) {
      setSaveMessage('Error: Chairman name is required');
      setIsSaving(false);
      return;
    }
    
    if (!chairmanPosition.trim()) {
      setSaveMessage('Error: Chairman position is required');
      setIsSaving(false);
      return;
    }
    
    try {
      console.log('Sending Chairman data:', { chairmanTitle, chairmanContent, chairmanName, chairmanPosition });
      
      const response = await chairmanService.updateChairman(
        chairmanTitle,
        chairmanContent,
        chairmanName,
        chairmanPosition,
        chairmanImage || undefined
      );
      
      console.log('Chairman Update Response:', response);
      
      if (response.success) {
        setChairmanData(response.data);
        setSaveMessage('Chairman data updated successfully!');
        setChairmanImage(null);
        setChairmanImagePreview(chairmanService.getImageUrl(response.data.icon));
        
        // Update original data and reset change flag
        setOriginalChairmanData({
          title: chairmanTitle,
          content: chairmanContent,
          name: chairmanName,
          position: chairmanPosition
        });
        setHasChairmanChanges(false);
        
        // Force refresh of the file input
        const fileInput = document.getElementById('chairmanImage') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSaveMessage(''), 5000);
      } else {
        const errorMessage = response.message || 'Unknown error occurred';
        setSaveMessage('Error updating Chairman data: ' + errorMessage);
        console.error('Chairman Update Error:', response);
      }
    } catch (error) {
      console.error('Error saving Chairman data:', error);
      setSaveMessage('Error saving Chairman data changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChairman = () => {
    if (chairmanData) {
      setChairmanTitle(chairmanData.title);
      setChairmanContent(chairmanData.content);
      setChairmanName(chairmanData.ceo_name);
      setChairmanPosition(chairmanData.ceo_position);
      setChairmanImagePreview(chairmanService.getImageUrl(chairmanData.icon));
    } else {
      setChairmanTitle('Chairman');
      setChairmanContent('');
      setChairmanName('');
      setChairmanPosition('');
      setChairmanImagePreview('/assets/images/md-ceo.svg');
    }
    setChairmanImage(null);
    setSaveMessage('');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSaveMessage('Error: Please select an image file');
        return;
      }
      
      // Validate file size (25MB limit)
      if (file.size > 50 * 1024 * 1024) {
        setSaveMessage('Error: Image size must be less than 25MB');
        return;
      }
      
      // Directly set the file without cropping
      setCeoImage(file);
      const previewUrl = URL.createObjectURL(file);
      setCeoImagePreview(previewUrl);
    }
  };

  // Application handlers
  const handleAddApplication = () => {
    const newApp: ApplicationData = {
      type: 'application',
      title: '',
      link: '',
      is_visible: true,
      sort_order: applications.length + 1
    };
    setEditingApp(newApp);
  };

  const handleEditApplication = (app: ApplicationData) => {
    setEditingApp({ ...app });
  };

  const handleDeleteApplicationClick = (app: ApplicationData) => {
    setAppToDelete(app);
    setShowDeleteAppModal(true);
  };

  const handleDeleteApplicationConfirm = async () => {
    if (!appToDelete || !appToDelete.id) return;

    setIsSaving(true);
    try {
      const response = await applicationService.deleteApplication(appToDelete.id);
      if (response.success) {
        setSaveMessage('Application deleted successfully');
        await loadApplications(); // Reload applications
      } else {
        setSaveMessage('Error: ' + response.message);
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      setSaveMessage('Error deleting application');
    } finally {
      setIsSaving(false);
      setAppToDelete(null);
    }
  };

  const handleSaveApplication = async () => {
    if (!editingApp) return;

    if (!editingApp.title.trim()) {
      setSaveMessage('Error: Application title is required');
      return;
    }

    if (!editingApp.link.trim()) {
      setSaveMessage('Error: Application link is required');
      return;
    }

    setIsSaving(true);
    try {
      let response;
      if (editingApp.id) {
        // Update existing application
        response = await applicationService.updateApplication(
          editingApp.id,
          editingApp.title,
          editingApp.link,
          editingApp.is_visible,
          editingApp.sort_order,
          appIconFile || undefined
        );
      } else {
        // Create new application
        response = await applicationService.createApplication(
          editingApp.title,
          editingApp.link,
          editingApp.is_visible,
          editingApp.sort_order,
          appIconFile || undefined
        );
      }

      if (response.success) {
        setSaveMessage('Application saved successfully');
        setEditingApp(null);
        setAppIconFile(null);
        await loadApplications(); // Reload applications
      } else {
        setSaveMessage('Error: ' + response.message);
      }
    } catch (error) {
      console.error('Error saving application:', error);
      setSaveMessage('Error saving application');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAppIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSaveMessage('Error: Please select an image file');
        return;
      }
      
      // Validate file size (25MB limit for icons)
      if (file.size > 50 * 1024 * 1024) {
        setSaveMessage('Error: Image size must be less than 25MB');
        return;
      }
      
      // Directly set the file without cropping
      setAppIconFile(file);
    }
  };

  const handleToggleVisibility = async (app: ApplicationData) => {
    setIsSaving(true);
    try {
      const response = await applicationService.updateApplication(
        app.id!,
        undefined,
        undefined,
        !app.is_visible,
        undefined
      );
      
      if (response.success) {
        await loadApplications(); // Reload applications
      } else {
        setSaveMessage('Error: ' + response.message);
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
      setSaveMessage('Error updating application');
    } finally {
      setIsSaving(false);
    }
  };

  // Change detection functions
  const checkBannerChanges = () => {
    if (!originalBannerData) return false;
    return bannerContent !== originalBannerData.content || 
           bannerVisible !== originalBannerData.visible;
  };

  const checkCeoChanges = () => {
    if (!originalCeoData) return false;
    return ceoTitle !== originalCeoData.title ||
           ceoContent !== originalCeoData.content ||
           ceoName !== originalCeoData.name ||
           ceoPosition !== originalCeoData.position ||
           ceoImage !== null; // Image change
  };

  const checkChairmanChanges = () => {
    if (!originalChairmanData) return false;
    return chairmanTitle !== originalChairmanData.title ||
           chairmanContent !== originalChairmanData.content ||
           chairmanName !== originalChairmanData.name ||
           chairmanPosition !== originalChairmanData.position ||
           chairmanImage !== null; // Image change
  };

  // Update change flags when form data changes
  React.useEffect(() => {
    setHasBannerChanges(checkBannerChanges());
  }, [bannerContent, bannerVisible, originalBannerData]);

  React.useEffect(() => {
    setHasCeoChanges(checkCeoChanges());
  }, [ceoTitle, ceoContent, ceoName, ceoPosition, ceoImage, originalCeoData]);

  React.useEffect(() => {
    setHasChairmanChanges(checkChairmanChanges());
  }, [chairmanTitle, chairmanContent, chairmanName, chairmanPosition, chairmanImage, originalChairmanData]);

  // Revert functions
  const revertBannerChanges = () => {
    if (originalBannerData) {
      setBannerContent(originalBannerData.content);
      setBannerVisible(originalBannerData.visible);
      setHasBannerChanges(false);
      setSaveMessage('Banner changes reverted');
      // Auto-hide revert message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const revertCeoChanges = () => {
    if (originalCeoData) {
      setCeoTitle(originalCeoData.title);
      setCeoContent(originalCeoData.content);
      setCeoName(originalCeoData.name);
      setCeoPosition(originalCeoData.position);
      setCeoImage(null);
      setHasCeoChanges(false);
      setSaveMessage('CEO changes reverted');
      // Auto-hide revert message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const revertChairmanChanges = () => {
    if (originalChairmanData) {
      setChairmanTitle(originalChairmanData.title);
      setChairmanContent(originalChairmanData.content);
      setChairmanName(originalChairmanData.name);
      setChairmanPosition(originalChairmanData.position);
      setChairmanImage(null);
      setHasChairmanChanges(false);
      setSaveMessage('Chairman changes reverted');
      // Auto-hide revert message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const revertApplicationChanges = () => {
    setPendingChanges(new Map());
    setEditingRowId(null);
    setEditingRowData(null);
    loadApplications(); // Reload original data
    setSaveMessage('Application changes reverted');
    // Auto-hide revert message after 3 seconds
    setTimeout(() => setSaveMessage(''), 3000);
  };

  // Check if any section has changes
  const hasAnyChanges = () => {
    if (activeSection === 'banner') return hasBannerChanges;
    if (activeSection === 'ceo') return hasCeoChanges;
    if (activeSection === 'chairman') return hasChairmanChanges;
    if (activeSection === 'applications') return pendingChanges.size > 0;
    if (activeSection === 'image-banners') return false; // BannerManagement handles its own state
    return false;
  };

  // Inline editing handlers
  const handleStartInlineEdit = (app: ApplicationData) => {
    setEditingRowId(app.id!);
    setEditingRowData({ ...app });
  };

  const handleCancelInlineEdit = () => {
    setEditingRowId(null);
    setEditingRowData(null);
  };

  const handleSaveInlineEdit = () => {
    if (!editingRowData || !editingRowId) return;

    if (!editingRowData.title.trim()) {
      setSaveMessage('Error: Application title is required');
      return;
    }

    if (!editingRowData.link.trim()) {
      setSaveMessage('Error: Application link is required');
      return;
    }

    // Add to pending changes
    setPendingChanges(prev => {
      const newChanges = new Map(prev);
      newChanges.set(editingRowId, editingRowData);
      return newChanges;
    });

    // Update local state immediately for UI feedback
    setApplications(prev => prev.map(app => 
      app.id === editingRowId ? editingRowData : app
    ));

    setEditingRowId(null);
    setEditingRowData(null);
    setSaveMessage('Changes staged. Click "Save All Changes" to apply.');
  };

  const handleSaveAllChanges = async () => {
    if (pendingChanges.size === 0) {
      setSaveMessage('No changes to save');
      return;
    }

    setIsSaving(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const [id, data] of Array.from(pendingChanges.entries())) {
        try {
          const response = await applicationService.updateApplication(
            id,
            data.title,
            data.link,
            data.is_visible,
            data.sort_order
          );
          
          if (response.success) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to update application ${id}:`, response.message);
          }
        } catch (error) {
          errorCount++;
          console.error(`Error updating application ${id}:`, error);
        }
      }

      if (successCount > 0) {
        setSaveMessage(`Successfully saved ${successCount} application(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
        setPendingChanges(new Map()); // Clear pending changes
        await loadApplications(); // Reload to get fresh data
        
        // Trigger homepage refresh by dispatching a custom event
        window.dispatchEvent(new CustomEvent('applicationsUpdated'));
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSaveMessage(''), 5000);
      } else {
        setSaveMessage('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error in bulk save:', error);
      setSaveMessage('Error saving changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, app: ApplicationData) => {
    setDraggedItem(app);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetApp: ApplicationData) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetApp.id) {
      setDraggedItem(null);
      return;
    }

    // Reorder applications
    const newApplications = [...applications];
    const draggedIndex = newApplications.findIndex(app => app.id === draggedItem.id);
    const targetIndex = newApplications.findIndex(app => app.id === targetApp.id);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      // Remove dragged item
      const [removed] = newApplications.splice(draggedIndex, 1);
      // Insert at target position
      newApplications.splice(targetIndex, 0, removed);

      // Update sort orders
      const updatedApps = newApplications.map((app, index) => ({
        ...app,
        sort_order: index + 1
      }));

      setApplications(updatedApps);

      // Add all reordered items to pending changes
      const newPendingChanges = new Map(pendingChanges);
      updatedApps.forEach(app => {
        if (app.sort_order !== applications.find(orig => orig.id === app.id)?.sort_order) {
          newPendingChanges.set(app.id!, app);
        }
      });
      setPendingChanges(newPendingChanges);

      setSaveMessage(`Reordered applications. Click "Save All Changes" to apply.`);
    }

    setDraggedItem(null);
  };

  return (
    <>
      {/* Loader */}
      {(isLoading || isSaving || isLoadingPermissions) && (
        <Loader message={isSaving ? 'Saving changes...' : 'Loading content...'} />
      )}
      
      {/* Common Header */}
      <CommonHeader showEditButton={false} />

      {/* Main Content */}
      <main className="edit-content-main">
        <div className="container">
          <div className="edit-content-layout">
            {/* Left Sidebar */}
            <aside className="edit-sidebar">
              <h3>Content Sections</h3>
              <nav className="section-nav">
                {hasTabPermission('banner') && (
                  <button 
                    className={`section-btn ${activeSection === 'banner' ? 'active' : ''}`}
                    onClick={() => setActiveSection('banner')}
                  >
                    <i className="icon-banner"></i>
                    Banner/Ticker
                  </button>
                )}
                {hasTabPermission('ceo') && (
                  <button 
                    className={`section-btn ${activeSection === 'ceo' ? 'active' : ''}`}
                    onClick={() => setActiveSection('ceo')}
                  >
                    <i className="icon-user"></i>
                    CEO Message
                  </button>
                )}
                {hasTabPermission('chairman') && (
                  <button 
                    className={`section-btn ${activeSection === 'chairman' ? 'active' : ''}`}
                    onClick={() => setActiveSection('chairman')}
                  >
                    <i className="icon-user"></i>
                    Chairman Message
                  </button>
                )}
                {hasTabPermission('applications') && (
                  <button 
                    className={`section-btn ${activeSection === 'applications' ? 'active' : ''}`}
                    onClick={() => setActiveSection('applications')}
                  >
                    <i className="icon-apps"></i>
                    Applications
                  </button>
                )}
                {hasTabPermission('image-banners') && (
                  <button 
                    className={`section-btn ${activeSection === 'image-banners' ? 'active' : ''}`}
                    onClick={() => setActiveSection('image-banners')}
                  >
                    <i className="icon-image"></i>
                    Image Banners
                  </button>
                )}
                {hasTabPermission('leaders') && (
                  <button 
                    className={`section-btn ${activeSection === 'leaders' ? 'active' : ''}`}
                    onClick={() => setActiveSection('leaders')}
                  >
                    <i className="icon-user"></i>
                    Our Leaders
                  </button>
                )}
                {hasTabPermission('updates') && (
                  <button 
                    className={`section-btn ${activeSection === 'updates' ? 'active' : ''}`}
                    onClick={() => setActiveSection('updates')}
                  >
                    <i className="icon-news"></i>
                    JWIL Updates
                  </button>
                )}
                {hasTabPermission('policies') && (
                  <button 
                    className={`section-btn ${activeSection === 'policies' ? 'active' : ''}`}
                    onClick={() => setActiveSection('policies')}
                  >
                    <i className="icon-document"></i>
                    Company Policies
                  </button>
                )}
                {hasTabPermission('policies') && (
                  <button 
                    className={`section-btn ${activeSection === 'policy-categories' ? 'active' : ''}`}
                    onClick={() => setActiveSection('policy-categories')}
                  >
                    <i className="icon-tag"></i>
                    Policy Categories
                  </button>
                )}
                {hasTabPermission('policies') && (
                  <button 
                    className={`section-btn ${activeSection === 'e-library' ? 'active' : ''}`}
                    onClick={() => setActiveSection('e-library')}
                  >
                    <i className="icon-document"></i>
                    E-Library
                  </button>
                )}
                {hasTabPermission('policies') && (
                  <button 
                    className={`section-btn ${activeSection === 'library-categories' ? 'active' : ''}`}
                    onClick={() => setActiveSection('library-categories')}
                  >
                    <i className="icon-tag"></i>
                    E-Library Categories
                  </button>
                )}
                {hasTabPermission('newsletter') && (
                  <button 
                    className={`section-btn ${activeSection === 'newsletter' ? 'active' : ''}`}
                    onClick={() => setActiveSection('newsletter')}
                  >
                    <i className="icon-news"></i>
                    Newsletter
                  </button>
                )}
                {hasTabPermission('gallery') && (
                  <button 
                    className={`section-btn ${activeSection === 'gallery' ? 'active' : ''}`}
                    onClick={() => setActiveSection('gallery')}
                  >
                    <i className="icon-snippet"></i>
                    Gallery
                  </button>
                )}
                {/* {hasTabPermission('events') && (
                  <button 
                    className={`section-btn ${activeSection === 'events' ? 'active' : ''}`}
                    onClick={() => setActiveSection('events')}
                  >
                    <i className="icon-calendar"></i>
                    Events
                  </button>
                )} */}
                {hasTabPermission('rewards') && (
                  <button 
                    className={`section-btn ${activeSection === 'rewards' ? 'active' : ''}`}
                    onClick={() => setActiveSection('rewards')}
                  >
                    <i className="icon-trophy"></i>
                    Rewards & Recognition
                  </button>
                )}
                {hasTabPermission('roles') && (
                  <button 
                    className={`section-btn ${activeSection === 'roles' ? 'active' : ''}`}
                    onClick={() => setActiveSection('roles')}
                  >
                    <i className="icon-shield"></i>
                    Role Management
                  </button>
                )}
                {hasTabPermission('users') && (
                  <button 
                    className={`section-btn ${activeSection === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveSection('users')}
                  >
                    <i className="icon-user"></i>
                    User Management
                  </button>
                )}
              </nav>
            </aside>

            {/* Main Edit Area */}
            <div className="edit-main-area">
              <div className="edit-header">
                <h2>
                  {activeSection === 'banner' && 'Edit Banner/Ticker'}
                  {activeSection === 'ceo' && 'Edit CEO Message'}
                  {activeSection === 'chairman' && 'Edit Chairman Message'}
                  {activeSection === 'applications' && 'Edit Applications'}
                  {activeSection === 'image-banners' && 'Edit Image Banners'}
                  {activeSection === 'leaders' && 'Manage Our Leaders'}
                  {activeSection === 'updates' && 'Edit JWIL Updates'}
                  {activeSection === 'policies' && 'Edit Company Policies'}
                  {activeSection === 'policy-categories' && 'Manage Policy Categories'}
                  {activeSection === 'e-library' && 'Edit E-Library'}
                  {activeSection === 'library-categories' && 'Manage E-Library Categories'}
                  {activeSection === 'newsletter' && 'Manage Newsletter'}
                  {activeSection === 'gallery' && 'Edit Gallery'}
                  {/* {activeSection === 'events' && 'Edit Events'} */}
                  {activeSection === 'rewards' && 'Edit Rewards & Recognition'}
                  {activeSection === 'roles' && 'Role Management'}
                  {activeSection === 'users' && 'User Management'}
                </h2>
                <div className="edit-actions">
                  {['image-banners', 'updates', 'policies', 'policy-categories', 'e-library', 'library-categories', 'newsletter', 'gallery', /* 'events', */ 'rewards', 'roles', 'users'].includes(activeSection) && (
                    <button
                      onClick={() => window.location.href = '/home'}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #6c757d',
                        backgroundColor: 'white',
                        color: '#6c757d',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Back
                    </button>
                  )}
                  {!['image-banners', 'updates', 'policies', 'policy-categories', 'e-library', 'library-categories', 'newsletter', 'gallery', /* 'events', */ 'rewards', 'roles', 'users'].includes(activeSection) && (
                  <div className="save-section" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button 
                      className={hasAnyChanges() ? "btn-cancel" : "btn-back"}
                      onClick={() => {
                        if (hasAnyChanges()) {
                          // Show cancel confirmation and revert changes
                          if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                            if (activeSection === 'banner') {
                              revertBannerChanges();
                            } else if (activeSection === 'ceo') {
                              revertCeoChanges();
                            } else if (activeSection === 'chairman') {
                              revertChairmanChanges();
                            } else if (activeSection === 'applications') {
                              revertApplicationChanges();
                            } else if (activeSection === 'image-banners') {
                              // BannerManagement handles its own state
                            }
                          }
                        } else {
                          // Navigate back to home
                          window.location.href = '/home';
                        }
                      }}
                      disabled={isLoading || isSaving}
                      style={{
                        padding: '8px 16px',
                        border: hasAnyChanges() ? '1px solid #dc3545' : '1px solid #6c757d',
                        backgroundColor: 'white',
                        color: hasAnyChanges() ? '#dc3545' : '#6c757d',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {hasAnyChanges() ? 'Cancel' : 'Back'}
                    </button>
                    
                    <button 
                      className="btn-save"
                      onClick={() => {
                        if (activeSection === 'banner') {
                          handleSaveBanner();
                        } else if (activeSection === 'ceo') {
                          handleSaveCeo();
                        } else if (activeSection === 'chairman') {
                          handleSaveChairman();
                        } else if (activeSection === 'applications') {
                          handleSaveAllChanges();
                        } else if (activeSection === 'image-banners') {
                          // BannerManagement handles its own save operations
                        }
                      }}
                      disabled={isSaving || !hasAnyChanges()}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        backgroundColor: hasAnyChanges() ? '#28a745' : '#e9ecef',
                        color: hasAnyChanges() ? 'white' : '#6c757d',
                        borderRadius: '4px',
                        cursor: hasAnyChanges() ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: '500',
                        opacity: hasAnyChanges() ? 1 : 0.6
                      }}
                    >
                      {isSaving ? 'Saving...' : 
                       activeSection === 'applications' && pendingChanges.size > 0 ? 
                       `Save Changes (${pendingChanges.size})` : 'Save Changes'}
                    </button>
                  </div>
                  )}
                </div>
              </div>

              <div className="edit-content-area">
                {/* Banner Section */}
                {activeSection === 'banner' && (
                  <div className="edit-section">
                    {/* Loading indicator */}
                    {isLoading && (
                      <div className="loading-message" style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#666',
                        fontStyle: 'italic'
                      }}>
                        Loading banner data...
                      </div>
                    )}
                    
                    {/* Save message */}
                    {saveMessage && (
                      <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`} style={{
                        padding: '12px',
                        marginBottom: '20px',
                        borderRadius: '4px',
                        backgroundColor: saveMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                        color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                        border: `1px solid ${saveMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
                        position: 'relative'
                      }}>
                        {saveMessage}
                        <button
                          onClick={() => setSaveMessage('')}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '8px',
                            background: 'none',
                            border: 'none',
                            fontSize: '16px',
                            cursor: 'pointer',
                            color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Close message"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="bannerText">Banner Text</label>
                      <textarea 
                        id="bannerText"
                        className="form-control"
                        rows={3}
                        placeholder="Enter banner/ticker text..."
                        value={bannerContent}
                        onChange={(e) => setBannerContent(e.target.value)}
                        disabled={isLoading || isSaving}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="bannerVisible">Visibility</label>
                      <div className="checkbox-group">
                        <input 
                          type="checkbox" 
                          id="bannerVisible" 
                          checked={bannerVisible}
                          onChange={(e) => setBannerVisible(e.target.checked)}
                          disabled={isLoading || isSaving}
                        />
                        <label htmlFor="bannerVisible">Show banner on homepage</label>
                      </div>
                    </div>
                  </div>
                )}

                {/* CEO Message Section */}
                {activeSection === 'ceo' && (
                  <div className="edit-section">
                    {/* Loading indicator */}
                    {isLoading && (
                      <div className="loading-message" style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#666',
                        fontStyle: 'italic'
                      }}>
                        Loading CEO data...
                      </div>
                    )}
                    
                    {/* Save message */}
                    {saveMessage && (
                      <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`} style={{
                        padding: '12px',
                        marginBottom: '20px',
                        borderRadius: '4px',
                        backgroundColor: saveMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                        color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                        border: `1px solid ${saveMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
                        position: 'relative'
                      }}>
                        {saveMessage}
                        <button
                          onClick={() => setSaveMessage('')}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '8px',
                            background: 'none',
                            border: 'none',
                            fontSize: '16px',
                            cursor: 'pointer',
                            color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Close message"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="ceoTitle">Title</label>
                      <input 
                        type="text" 
                        id="ceoTitle"
                        className="form-control"
                        placeholder="Enter title..."
                        value={ceoTitle}
                        onChange={(e) => setCeoTitle(e.target.value)}
                        disabled={isLoading || isSaving}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="ceoName">CEO Name *</label>
                      <input 
                        type="text" 
                        id="ceoName"
                        className="form-control"
                        placeholder="Enter CEO name..."
                        value={ceoName}
                        onChange={(e) => setCeoName(e.target.value)}
                        disabled={isLoading || isSaving}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="ceoPosition">Designation/Position *</label>
                      <input 
                        type="text" 
                        id="ceoPosition"
                        className="form-control"
                        placeholder="Enter position/designation..."
                        value={ceoPosition}
                        onChange={(e) => setCeoPosition(e.target.value)}
                        disabled={isLoading || isSaving}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="ceoMessage">Message Content *</label>
                      <textarea 
                        id="ceoMessage"
                        className="form-control"
                        rows={6}
                        placeholder="Enter CEO message..."
                        value={ceoContent}
                        onChange={(e) => setCeoContent(e.target.value)}
                        disabled={isLoading || isSaving}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="ceoImage">CEO Image</label>
                      
                      {/* Current image preview */}
                      {ceoImagePreview && (
                        <div className="current-image-preview" style={{
                          marginBottom: '15px',
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#f9f9f9',
                          textAlign: 'center'
                        }}>
                          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
                            Current CEO Image (76×92px):
                          </p>
                          <div style={{
                            display: 'inline-block',
                            width: '76px',
                            height: '92px',
                            border: '2px solid #007bff',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            backgroundColor: 'white'
                          }}>
                            <img 
                              src={ceoImagePreview} 
                              alt="CEO" 
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onLoad={() => console.log('CEO edit preview image loaded successfully')}
                              onError={(e) => {
                                console.error('CEO edit preview image failed to load:', e);
                                console.log('Failed preview image src:', (e.target as HTMLImageElement).src);
                                console.log('Current ceoImagePreview value:', ceoImagePreview);
                              }}
                            />
                          </div>
                          <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#888' }}>
                            Exact size preview
                          </p>
                        </div>
                      )}

                      <div className="file-upload">
                        <input 
                          type="file" 
                          id="ceoImage" 
                          accept="image/*"
                          onChange={handleImageChange}
                          disabled={isLoading || isSaving}
                        />
                        <label htmlFor="ceoImage" className="file-upload-label">
                          <i className="icon-upload"></i>
                          {ceoImage ? ceoImage.name : 'Choose New Image'}
                        </label>
                      </div>
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        <strong>Supported formats:</strong> JPG, PNG, GIF. <strong>Max size:</strong> 5MB<br />
                        <strong>Image will be cropped to 76×92 pixels</strong> for optimal display
                      </small>
                    </div>
                  </div>
                )}

                {/* Chairman Message Section */}
                {activeSection === 'chairman' && (
                  <div className="edit-section">
                    {/* Loading indicator */}
                    {isLoading && (
                      <div className="loading-message" style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#666',
                        fontStyle: 'italic'
                      }}>
                        Loading Chairman data...
                      </div>
                    )}
                    
                    {/* Save message */}
                    {saveMessage && (
                      <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`} style={{
                        padding: '12px',
                        marginBottom: '20px',
                        borderRadius: '4px',
                        backgroundColor: saveMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                        color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                        border: `1px solid ${saveMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
                        position: 'relative'
                      }}>
                        {saveMessage}
                        <button
                          onClick={() => setSaveMessage('')}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '8px',
                            background: 'none',
                            border: 'none',
                            fontSize: '16px',
                            cursor: 'pointer',
                            color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Close message"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="chairmanTitle">Title</label>
                      <input 
                        type="text" 
                        id="chairmanTitle"
                        className="form-control"
                        placeholder="Enter title..."
                        value={chairmanTitle}
                        onChange={(e) => setChairmanTitle(e.target.value)}
                        disabled={isLoading || isSaving}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="chairmanName">Chairman Name *</label>
                      <input 
                        type="text" 
                        id="chairmanName"
                        className="form-control"
                        placeholder="Enter Chairman name..."
                        value={chairmanName}
                        onChange={(e) => setChairmanName(e.target.value)}
                        disabled={isLoading || isSaving}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="chairmanPosition">Designation/Position *</label>
                      <input 
                        type="text" 
                        id="chairmanPosition"
                        className="form-control"
                        placeholder="Enter position/designation..."
                        value={chairmanPosition}
                        onChange={(e) => setChairmanPosition(e.target.value)}
                        disabled={isLoading || isSaving}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="chairmanMessage">Message Content *</label>
                      <textarea 
                        id="chairmanMessage"
                        className="form-control"
                        rows={6}
                        placeholder="Enter Chairman message..."
                        value={chairmanContent}
                        onChange={(e) => setChairmanContent(e.target.value)}
                        disabled={isLoading || isSaving}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="chairmanImage">Chairman Image</label>
                      
                      {/* Current image preview */}
                      {chairmanImagePreview && (
                        <div className="current-image-preview" style={{
                          marginBottom: '15px',
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#f9f9f9',
                          textAlign: 'center'
                        }}>
                          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
                            Current Chairman Image (76×92px):
                          </p>
                          <div style={{
                            display: 'inline-block',
                            width: '76px',
                            height: '92px',
                            border: '2px solid #007bff',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            backgroundColor: 'white'
                          }}>
                            <img 
                              src={chairmanImagePreview} 
                              alt="Chairman" 
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onLoad={() => console.log('Chairman edit preview image loaded successfully')}
                              onError={(e) => {
                                console.error('Chairman edit preview image failed to load:', e);
                                console.log('Failed preview image src:', (e.target as HTMLImageElement).src);
                                console.log('Current chairmanImagePreview value:', chairmanImagePreview);
                              }}
                            />
                          </div>
                          <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#888' }}>
                            Exact size preview
                          </p>
                        </div>
                      )}

                      <div className="file-upload">
                        <input 
                          type="file" 
                          id="chairmanImage" 
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (!file.type.startsWith('image/')) {
                                setSaveMessage('Error: Please select an image file');
                                return;
                              }
                              if (file.size > 50 * 1024 * 1024) {
                                setSaveMessage('Error: Image size must be less than 25MB');
                                return;
                              }
                              setChairmanImage(file);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setChairmanImagePreview(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          disabled={isLoading || isSaving}
                        />
                        <label htmlFor="chairmanImage" className="file-upload-label">
                          <i className="icon-upload"></i>
                          {chairmanImage ? chairmanImage.name : 'Choose New Image'}
                        </label>
                      </div>
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        <strong>Supported formats:</strong> JPG, PNG, GIF. <strong>Max size:</strong> 5MB<br />
                        <strong>Image will be cropped to 76×92 pixels</strong> for optimal display
                      </small>
                    </div>
                  </div>
                )}

                {/* Applications Section */}
                {activeSection === 'applications' && (() => {
                  // Filter applications based on search query
                  const filteredApps = applications.filter(app => {
                    const query = appSearchQuery.toLowerCase();
                    return app.title.toLowerCase().includes(query);
                  });

                  // Pagination logic
                  const indexOfLastApp = appCurrentPage * appItemsPerPage;
                  const indexOfFirstApp = indexOfLastApp - appItemsPerPage;
                  const currentApps = filteredApps.slice(indexOfFirstApp, indexOfLastApp);
                  const totalPages = Math.ceil(filteredApps.length / appItemsPerPage);

                  const handleAppPageChange = (pageNumber: number) => {
                    setAppCurrentPage(pageNumber);
                  };

                  const handleAppSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    setAppSearchQuery(e.target.value);
                    setAppCurrentPage(1);
                  };

                  return (
                  <div className="edit-section">
                    <div className="applications-list">
                      <div className="list-header">
                        <h4>Application Management</h4>
                        <button 
                          className="btn-add"
                          onClick={handleAddApplication}
                          disabled={isLoading || isSaving}
                        >
                          <i className="icon-plus"></i>
                          Add Application
                        </button>
                      </div>

                      {/* Save message for Applications */}
                      {saveMessage && (
                        <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`} style={{
                          padding: '12px',
                          marginBottom: '20px',
                          borderRadius: '4px',
                          backgroundColor: saveMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                          color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                          border: `1px solid ${saveMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
                          position: 'relative'
                        }}>
                          {saveMessage}
                          <button
                            onClick={() => setSaveMessage('')}
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '8px',
                              background: 'none',
                              border: 'none',
                              fontSize: '16px',
                              cursor: 'pointer',
                              color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                              padding: '0',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Close message"
                          >
                            ×
                          </button>
                        </div>
                      )}

                      {/* Search Bar */}
                      <div style={{ marginTop: '20px', marginBottom: '15px' }}>
                        <div style={{ position: 'relative', maxWidth: '400px' }}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search by title..."
                            value={appSearchQuery}
                            onChange={handleAppSearchChange}
                            style={{
                              paddingLeft: '35px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              width: '100%',
                              padding: '8px 8px 8px 35px'
                            }}
                          />
                          <i 
                            className="fas fa-search" 
                            style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6c757d'
                            }}
                          ></i>
                        </div>
                        {appSearchQuery && (
                          <div style={{ marginTop: '8px', fontSize: '14px', color: '#6c757d' }}>
                            Found {filteredApps.length} application{filteredApps.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>

                      {/* Applications Table */}
                      <div className="applications-table" style={{ marginTop: '20px', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', tableLayout: 'fixed' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '8%' }}>Icon</th>
                              <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '25%' }}>Title</th>
                              <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '35%' }}>Link</th>
                              <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '8%' }}>Visible</th>
                              <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '10%' }}>Sort</th>
                              <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '14%' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentApps.length === 0 ? (
                              <tr>
                                <td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: '#999', border: '1px solid #ddd' }}>
                                  {appSearchQuery ? 'No applications found matching your search' : 'No applications available'}
                                </td>
                              </tr>
                            ) : (
                              currentApps.map((app) => (
                              <tr 
                                key={app.id} 
                                draggable={editingRowId === null}
                                onDragStart={(e) => handleDragStart(e, app)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, app)}
                                style={{ 
                                  borderBottom: '1px solid #ddd',
                                  backgroundColor: editingRowId === app.id ? '#f8f9fa' : 
                                    (pendingChanges.has(app.id!) ? '#fff3cd' : 'transparent'),
                                  cursor: editingRowId === null ? 'grab' : 'default',
                                  opacity: draggedItem?.id === app.id ? 0.5 : 1
                                }}
                              >
                                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                  <img 
                                    src={applicationService.getImageUrl(app.icon || null)} 
                                    alt={app.title}
                                    style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                                  />
                                </td>
                                
                                {/* Title - Inline editable */}
                                <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {editingRowId === app.id ? (
                                    <input
                                      type="text"
                                      value={editingRowData?.title || ''}
                                      onChange={(e) => setEditingRowData(prev => prev ? { ...prev, title: e.target.value } : null)}
                                      style={{
                                        width: '100%',
                                        padding: '4px',
                                        border: '1px solid #ddd',
                                        borderRadius: '2px'
                                      }}
                                    />
                                  ) : (
                                    app.title
                                  )}
                                </td>
                                
                                {/* Link - Inline editable */}
                                <td style={{ padding: '8px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {editingRowId === app.id ? (
                                    <input
                                      type="text"
                                      value={editingRowData?.link || ''}
                                      onChange={(e) => setEditingRowData(prev => prev ? { ...prev, link: e.target.value } : null)}
                                      style={{
                                        width: '100%',
                                        padding: '4px',
                                        border: '1px solid #ddd',
                                        borderRadius: '2px'
                                      }}
                                    />
                                  ) : (
                                    <a 
                                      href={app.link} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ color: '#007bff', textDecoration: 'none' }}
                                    >
                                      {app.link.length > 30 ? app.link.substring(0, 30) + '...' : app.link}
                                    </a>
                                  )}
                                </td>
                                
                                {/* Visibility Toggle */}
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                                  <button
                                    onClick={() => handleToggleVisibility(app)}
                                    disabled={isSaving || editingRowId === app.id}
                                    style={{
                                      padding: '3px 8px',
                                      border: 'none',
                                      borderRadius: '4px',
                                      backgroundColor: app.is_visible ? '#28a745' : '#dc3545',
                                      color: 'white',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {app.is_visible ? 'Yes' : 'No'}
                                  </button>
                                </td>
                                
                                {/* Sort Order - Inline editable with drag handle */}
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                    {editingRowId !== app.id && (
                                      <span style={{ cursor: 'grab', color: '#666', fontSize: '14px' }} title="Drag to reorder">
                                        ⋮⋮
                                      </span>
                                    )}
                                    {editingRowId === app.id ? (
                                      <input
                                        type="number"
                                        value={editingRowData?.sort_order || 0}
                                        onChange={(e) => setEditingRowData(prev => prev ? { ...prev, sort_order: parseInt(e.target.value) || 0 } : null)}
                                        style={{
                                          width: '60px',
                                          padding: '4px',
                                          border: '1px solid #ddd',
                                          borderRadius: '2px',
                                          textAlign: 'center'
                                        }}
                                      />
                                    ) : (
                                      <span>{app.sort_order}</span>
                                    )}
                                  </div>
                                </td>
                                
                                {/* Actions */}
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                                  {editingRowId === app.id ? (
                                    // Save/Cancel buttons when editing
                                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                      <button
                                        onClick={handleSaveInlineEdit}
                                        disabled={isSaving}
                                        style={{
                                          padding: '4px 8px',
                                          border: 'none',
                                          backgroundColor: '#28a745',
                                          color: 'white',
                                          borderRadius: '3px',
                                          cursor: 'pointer',
                                          fontSize: '11px',
                                          fontWeight: '600'
                                        }}
                                        title="Save changes"
                                      >
                                        ✓
                                      </button>
                                      <button
                                        onClick={handleCancelInlineEdit}
                                        disabled={isSaving}
                                        style={{
                                          padding: '4px 8px',
                                          border: 'none',
                                          backgroundColor: '#6c757d',
                                          color: 'white',
                                          borderRadius: '3px',
                                          cursor: 'pointer',
                                          fontSize: '11px',
                                          fontWeight: '600'
                                        }}
                                        title="Cancel editing"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ) : (
                                    // Edit/Delete buttons when not editing
                                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                      <button
                                        onClick={() => handleEditApplication(app)}
                                        disabled={isSaving || editingRowId !== null}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: (isSaving || editingRowId !== null) ? 'not-allowed' : 'pointer',
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          fontSize: '16px',
                                          transition: 'all 0.3s ease',
                                          opacity: (isSaving || editingRowId !== null) ? 0.5 : 1
                                        }}
                                        title="Edit application"
                                        onMouseEnter={(e) => !(isSaving || editingRowId !== null) && (e.currentTarget.style.background = '#e3f2fd')}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                      >
                                        ✏️
                                      </button>
                                      <button
                                        onClick={() => handleDeleteApplicationClick(app)}
                                        disabled={isSaving || editingRowId !== null}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: (isSaving || editingRowId !== null) ? 'not-allowed' : 'pointer',
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          fontSize: '16px',
                                          transition: 'all 0.3s ease',
                                          opacity: (isSaving || editingRowId !== null) ? 0.5 : 1
                                        }}
                                        title="Delete application"
                                        onMouseEnter={(e) => !(isSaving || editingRowId !== null) && (e.currentTarget.style.background = '#ffebee')}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )))}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {filteredApps.length > appItemsPerPage && (
                          <div style={{ 
                            marginTop: '20px', 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            padding: '10px 0'
                          }}>
                            <div style={{ fontSize: '14px', color: '#6c757d' }}>
                              Showing {indexOfFirstApp + 1} to {Math.min(indexOfLastApp, filteredApps.length)} of {filteredApps.length} applications
                            </div>
                            <div style={{ display: 'flex', gap: '5px' }}>
                              <button
                                onClick={() => handleAppPageChange(appCurrentPage - 1)}
                                disabled={appCurrentPage === 1}
                                style={{
                                  padding: '6px 12px',
                                  border: '1px solid #ddd',
                                  backgroundColor: appCurrentPage === 1 ? '#f8f9fa' : 'white',
                                  color: appCurrentPage === 1 ? '#6c757d' : '#007bff',
                                  cursor: appCurrentPage === 1 ? 'not-allowed' : 'pointer',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              >
                                Previous
                              </button>
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                  key={page}
                                  onClick={() => handleAppPageChange(page)}
                                  style={{
                                    padding: '6px 12px',
                                    border: '1px solid #ddd',
                                    backgroundColor: appCurrentPage === page ? '#007bff' : 'white',
                                    color: appCurrentPage === page ? 'white' : '#007bff',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    fontWeight: appCurrentPage === page ? 'bold' : 'normal'
                                  }}
                                >
                                  {page}
                                </button>
                              ))}
                              <button
                                onClick={() => handleAppPageChange(appCurrentPage + 1)}
                                disabled={appCurrentPage === totalPages}
                                style={{
                                  padding: '6px 12px',
                                  border: '1px solid #ddd',
                                  backgroundColor: appCurrentPage === totalPages ? '#f8f9fa' : 'white',
                                  color: appCurrentPage === totalPages ? '#6c757d' : '#007bff',
                                  cursor: appCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })()}

                {/* Image Banners Section */}
                {activeSection === 'image-banners' && (
                  <div className="edit-section">
                    <BannerManagement />
                  </div>
                )}

                {/* Our Leaders Section */}
                {activeSection === 'leaders' && (
                  <div className="edit-section">
                    <LeadersManagement />
                  </div>
                )}

                {/* JWIL Updates Section */}
                {activeSection === 'updates' && (
                  <div className="edit-section">
                    <JwilUpdatesManagement />
                  </div>
                )}

                {/* Company Policies Section */}
                {activeSection === 'policies' && (
                  <div className="edit-section">
                    <CompanyPoliciesManagement />
                  </div>
                )}

                {/* Policy Categories Section */}
                {activeSection === 'policy-categories' && (
                  <div className="edit-section">
                    <CategoryManagement type="policy" />
                  </div>
                )}

                {/* E-Library Section */}
                {activeSection === 'e-library' && (
                  <div className="edit-section">
                    <EProcedureManagement />
                  </div>
                )}

                {/* E-Library Categories Section */}
                {activeSection === 'library-categories' && (
                  <div className="edit-section">
                    <CategoryManagement type="library" />
                  </div>
                )}

                {/* Newsletter Section */}
                {activeSection === 'newsletter' && (
                  <div className="edit-section">
                    <NewsletterManagement />
                  </div>
                )}

                {/* Snippets Section */}
                {activeSection === 'gallery' && (
                  <div className="edit-section">
                    <SnippetAlbumManagement />
                  </div>
                )}

                {/* Events Section */}
                {/* {activeSection === 'events' && (
                  <div className="edit-section">
                    <EventsManagement />
                  </div>
                )} */}

                {/* Rewards & Recognition Section */}
                {activeSection === 'rewards' && (
                  <div className="edit-section edit-section-full-width">
                    <RewardRecognitionManagement />
                  </div>
                )}

                {/* Role Management Section */}
                {activeSection === 'roles' && (
                  <div className="edit-section edit-section-full-width">
                    <RoleManagement />
                  </div>
                )}

                {/* User Management Section */}
                {activeSection === 'users' && (
                  <div className="edit-section edit-section-full-width">
                    <UserManagement />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <CommonFooter />

      {/* Floating Chat Boat Icon */}
      <div className="floating-chat-boat">
        <button className="chat-boat-btn" aria-label="Chat Support" title="Chat with us">
          <img src="/assets/images/chat-boat.svg" alt="Chat Boat Icon" className="chat-boat-icon" />
        </button>
      </div>

      {/* Edit Application Modal */}
      {editingApp && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              {editingApp.id ? 'Edit Application' : 'Add New Application'}
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Title *
              </label>
              <input
                type="text"
                value={editingApp.title}
                onChange={(e) => setEditingApp({ ...editingApp, title: e.target.value })}
                placeholder="Application Name"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Link *
              </label>
              <input
                type="text"
                value={editingApp.link}
                onChange={(e) => setEditingApp({ ...editingApp, link: e.target.value })}
                placeholder="https://app.jwil.com"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Icon (24×24px)
              </label>
              <input
                type="file"
                id="appIcon"
                accept="image/*"
                onChange={handleAppIconChange}
                style={{ marginBottom: '10px' }}
              />
              {appIconFile && (
                <div style={{ marginTop: '10px' }}>
                  <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666' }}>
                    Preview (24×24px):
                  </p>
                  <img
                    src={URL.createObjectURL(appIconFile)}
                    alt="Icon preview"
                    style={{
                      width: '24px',
                      height: '24px',
                      border: '1px solid #ddd',
                      borderRadius: '2px'
                    }}
                  />
                </div>
              )}
              <small style={{ color: '#666', fontSize: '12px' }}>
                <strong>Supported formats:</strong> JPG, PNG, GIF. <strong>Max size:</strong> 2MB<br />
                <strong>Image will be cropped to 24×24 pixels</strong> for optimal display
              </small>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={editingApp.is_visible}
                  onChange={(e) => setEditingApp({ ...editingApp, is_visible: e.target.checked })}
                />
                <span style={{ fontWeight: 'bold' }}>Visible on homepage</span>
              </label>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Sort Order
              </label>
              <input
                type="number"
                value={editingApp.sort_order}
                onChange={(e) => setEditingApp({ ...editingApp, sort_order: parseInt(e.target.value) || 0 })}
                min="0"
                style={{
                  width: '100px',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setEditingApp(null);
                  setAppIconFile(null);
                }}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveApplication}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  backgroundColor: '#007bff',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {isSaving ? 'Saving...' : 'Save Application'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Application Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteAppModal}
        onClose={() => setShowDeleteAppModal(false)}
        onConfirm={handleDeleteApplicationConfirm}
        title="Delete Application"
        message="Are you sure you want to delete this application?"
        itemName={appToDelete?.title}
      />
    </>
  );
};

export default EditContent;
